import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { qrService, PaymentQRData } from '../lib/qr'
import { useWallet } from '../hooks/use-wallet'
import { useLocation } from 'wouter'
import { apiFetch, parseEthereumURI } from '@/utils'
import { isAddress } from 'viem'

interface QRScannerProps {
  mode?: 'payment' | 'menu' | 'wallet'
  onWalletScanned?: (address: string) => void
}

export default function QRScanner({
  mode = 'payment',
  onWalletScanned
}: QRScannerProps) {
  const [isScanning, setIsScanning] = useState(false)
  const [cameraStream, setCameraStream] = useState<any>()
  const [processingPayment, setProcessingPayment] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const scanningRef = useRef(false)
  const { toast } = useToast()
  const [, setLocation] = useLocation()

  useEffect(() => {
    return () => {
      stopScanning()
    }
  }, [])

  const stopScanning = () => {
    scanningRef.current = false
    if (videoRef.current?.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks()
      tracks.forEach((track) => track.stop())
      videoRef.current.srcObject = null
    }
    qrService.stopScanning()
    setIsScanning(false)
    setError(null)
  }

  const restartScanning = async () => {
    setError(null)
    await scanQRCode()
  }

  const startScanning = async () => {
    try {
      setError(null)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      })

      setIsScanning(true)
      setCameraStream(stream)
    } catch (error: any) {
      let errorMessage
      if (error?.message?.includes('denied')) {
        errorMessage =
          'Camera access denied. Please allow camera permissions to Scan QR.'
      }
      errorMessage =
        errorMessage ||
        (error instanceof Error ? error.message : 'Could not access camera')

      setError(errorMessage)
      toast({
        title: 'Camera Unavailable',
        description: errorMessage,
        variant: 'destructive'
      })
    }
  }

  const scanQRCode = async () => {
    if (!videoRef.current || !scanningRef.current) return

    try {
      const scannedData = await qrService.scanQR(videoRef.current)
      // Handle the scanned data based on mode
      if (mode === 'wallet') {
        // For wallet mode, expect a string address
        if (typeof scannedData === 'string') {
          await handleWalletAddress(scannedData)
        } else {
          throw new Error('Invalid QR code format for wallet address')
        }
      } else {
        // For other modes, expect PaymentQRData
        if (typeof scannedData === 'object') {
          await handleQRData(scannedData as PaymentQRData)
        } else {
          throw new Error('Invalid QR code format for payment/menu')
        }
      }
    } catch (error) {
      // Ignore QR detection errors and continue scanning
      if (
        error instanceof Error &&
        error.message.includes('No QR code found')
      ) {
        if (scanningRef.current) {
          requestAnimationFrame(() => scanQRCode())
        }
      } else {
        const errorMessage =
          error instanceof Error ? error.message : 'QR scanning failed'
        setError(errorMessage)
        toast({
          title: 'Scanning Error',
          description: errorMessage,
          variant: 'destructive'
        })
      }
    }
  }

  const handleWalletAddress = async (data: string) => {
    const scannedWalletAddress = parseEthereumURI(data) as string
    try {
      if (!isAddress(scannedWalletAddress)) {
        console.log('Not an address')
        throw new Error('Invalid wallet address format')
      }

      if (onWalletScanned) {
        onWalletScanned(scannedWalletAddress)
        toast({
          title: 'Address Scanned',
          description: 'Wallet address successfully scanned'
        })
      }
      stopScanning()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Invalid QR code'
      setError(errorMessage)
      toast({
        title: 'Invalid Address',
        description: errorMessage,
        variant: 'destructive'
      })
    }
  }

  const handleQRData = async (data: PaymentQRData) => {
    stopScanning()

    if (data.type === 'payment' && data.amount) {
      setProcessingPayment(true)
      try {
        if (!window.ethereum?.selectedAddress) {
          throw new Error('Wallet not connected')
        }

        // Send payment to hotel address
        const hotelWallet = import.meta.env.VITE_HOTEL_WALLET_ADDRESS
        // const success = await sendTokens(hotelWallet, data.amount)
        // if (!success) {
        //   throw new Error('Token transfer failed')
        // }

        // Record transaction
        // const response = await apiFetch('/v1/transactions', {
        //   method: 'POST',
        //   body: JSON.stringify({
        //     fromAddress: window.ethereum.selectedAddress,
        //     toAddress: hotelWallet,
        //     amount: data.amount,
        //     orderId: data.orderId
        //   })
        // })

        // if (!response.ok) {
        //   const errorData = await response.json()
        //   throw new Error(errorData.message || 'Failed to record transaction')
        // }

        toast({
          title: 'Payment Successful',
          description: `Paid ${data.amount} SECH`
        })
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Payment processing failed'
        setError(errorMessage)
        toast({
          title: 'Payment Failed',
          description: errorMessage,
          variant: 'destructive'
        })
      } finally {
        setProcessingPayment(false)
      }
    } else if (data.type === 'menu') {
      setLocation(`/menu`)
    }
  }

  useEffect(() => {
    const connectCameraComponent = async () => {
      if (!videoRef.current || !cameraStream) return

      videoRef.current.srcObject = cameraStream
      await videoRef?.current?.play()
      setIsScanning(true)
      scanningRef.current = true
      scanQRCode()
    }

    isScanning && !error && setTimeout(connectCameraComponent, 0)
  }, [videoRef.current, isScanning, cameraStream])

  return (
    <div className="flex flex-1 w-full my-0 ">
      {/* <h2 className="text-lg font-semibold">
        {mode === 'wallet' ? 'Scan Wallet Address' : 'Scan QR Code'}
      </h2> */}

      <Dialog
        open={isScanning}
        onOpenChange={(open) => !open && stopScanning()}>
        <Button
          onClick={startScanning}
          disabled={isScanning || processingPayment}
          className="w-full"
          type="button">
          {processingPayment
            ? 'Processing Payment...'
            : mode === 'wallet'
            ? 'Scan Wallet QR'
            : 'Scan QR Code'}
        </Button>

        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {mode === 'wallet' ? 'Scan Wallet Address' : 'Scan QR Code'}
            </DialogTitle>
          </DialogHeader>
          <Card className="relative aspect-square overflow-hidden">
            <video
              ref={videoRef}
              className="absolute inset-0 h-full w-full object-cover"
              playsInline
            />
            <div className="absolute inset-0 border-2 border-primary opacity-50" />
            {error && (
              <div className="absolute inset-0 flex items-center justify-center flex-col bg-background/80">
                <p className="text-sm text-destructive text-center p-4">
                  {error}
                </p>
                <Button onClick={restartScanning}>Try again</Button>
              </div>
            )}
          </Card>
        </DialogContent>
      </Dialog>
    </div>
  )
}
